<template>
  <div class="annotation" ref="annot">
    <input class="input" type="text" ref="input" :disabled="!interactable" :filled="isAnnotationFilled()"
      :error="error" v-model="value" @mousedown.prevent="openOptions()" @keydown.prevent="" :style="{
        fontSize: `${fontSize}px`
      }" />
    <div class="select-options" v-if="open" ref="options">
      <div class="select-option" v-for="option of getOptions()" :key="option">
        <CheckBox colorFilled="#EEE" colorLine="#000" :value="isCheckboxChecked(option)"
          @updateValue="setCheckboxChecked($event, option)" />
        <label>{{ option }}</label>
      </div>
    </div>
    <img src="@/assets/trash.png" alt="trash" class="icon-sm" v-if="showTrash"
      @mousedown.prevent.stop="$emit('deleteAnnotation')">
  </div>
</template>

<script>
import CheckBox from '../CheckBox.vue';

export default {
  name: "MultiSelectAnnotation",
  props: {
    annotation: Object,
    interactable: Boolean,
    showTrash: Boolean,
  },
  data() {
    return {
      value: '',
      error: false,
      fontSize: 20,
      open: false,
      selected: []
    }
  },
  mounted() {
    this.refreshAnnotation();
    window.addEventListener('mousedown', this.onMouseDown);
  },
  unmounted() {
    window.removeEventListener('mousedown', this.onMouseDown);
  },
  watch: {
    annotation: {
      handler: function () {
        this.refreshAnnotation();
      },
      deep: true
    }
  },
  methods: {
    updateValue(value) {
      this.value = value;
      // Parse the value back into selected array
      if (value && value.length > 0) {
        this.selected = value.split(', ').filter(v => v.length > 0);
      } else {
        this.selected = [];
      }
      setTimeout(() => {
        this.refreshAnnotation();
      }, 0)
    },
    onAnnotationChange() {
      this.$emit("onUpdateValue", this.value);
      this.refreshAnnotation();
    },
    isAnnotationFilled() {
      return this.value.length > 0;
    },
    checkAnnotationIsFilled() {
      if (this.annotation.optional) {
        return true;
      }

      if (this.isAnnotationFilled()) {
        return true;
      }

      this.error = true;
      return false;
    },
    setError(value) {
      this.error = value;
    },
    refreshAnnotation() {
      const inputRef = this.$refs.input;
      if (!inputRef) return;

      // Compute font size based on rect size
      const rect = inputRef.getBoundingClientRect();
      if (rect.height == 0) return;

      // Measure the width of the text
      const style = getComputedStyle(inputRef);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext('2d');
      context.font = `${rect.height}px ${style.fontFamily}`;
      const textWidth = context.measureText(inputRef.value).width;

      // Resize annotation to fit
      const ratio = rect.width / textWidth;
      if (ratio < 1) {
        inputRef.style.fontSize = rect.height * ratio + 'px';
      } else {
        inputRef.style.fontSize = rect.height + 'px';
      }
    },
    getOptions() {
      return this.annotation.options || [];
    },
    openOptions() {
      if (!this.interactable) return;
      setTimeout(() => {
        this.open = true;
      }, 1);
    },
    onMouseDown(event) {
      if (!this.open) return;

      const options = this.$refs.options;
      if (!options) return;

      const target = event.target;
      if (!options.contains(target)) {
        this.open = false;
      }
    },
    isCheckboxChecked(option) {
      return this.selected.includes(option);
    },
    setCheckboxChecked(isChecked, option) {
      // Remove option from selected
      this.selected = this.selected.filter(opt => opt != option);

      // Push it if needed
      if (isChecked) {
        this.selected.push(option);
      }

      // Recompute value
      this.value = this.selected.join(', ');
      this.onAnnotationChange();
    }
  },
  components: { CheckBox },
  emits: ['onUpdateValue', 'deleteAnnotation']
};
</script>

<style scoped>
.annotation {
  position: relative;
  display: flex;
  width: 100%;
  height: 25px;
  flex-direction: column;
  justify-content: flex-end;
}

.input {
  display: block;
  width: 100%;
  height: 1px;
  flex: 1;
  border: none;
  outline: none;
  background: var(--color-patient-field);
  opacity: 1;
  border-radius: 0px;
  padding: 0px !important;
  color: #000 !important;
  cursor: pointer;
}

.input[error="true"] {
  background: var(--color-patient-field-error);
}

.input[filled="true"] {
  background: var(--color-patient-field-filled);
}

.input::placeholder {
  opacity: 0.3;
  color: #000;
}

.input:disabled {
  pointer-events: none;
}

.icon-sm {
  display: block;
  position: absolute;
  top: calc(50% - 12.5px);
  left: -30px;
  height: 25px;
  filter: saturate(0);
}

.icon-sm:hover {
  cursor: pointer;
  filter: none;
}

.select-options {
  max-height: 400px;
  overflow-y: auto;
  position: absolute;
  bottom: 100%;
  left: 0;
  background: #FFF;
  box-shadow: 0px 0px 8px #00000044;
  z-index: 1000;
  padding: 10px;
  border-radius: 5px;
  min-width: 200px;
}

.select-option {
  padding: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.select-option .checkbox {
  flex-shrink: 0;
}

.select-option label {
  margin-left: 10px;
  font-size: 16px;
  color: #222;
}
</style>
